import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaDownload } from 'react-icons/fa';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PeopleIcon from '@mui/icons-material/People';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { FaSearch } from 'react-icons/fa';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../styles';
import {
  FiltersBar,
  EmployeeRow,
  EmployeeInfo,
  EmployeeName,
  EmployeeEmail,
  CertBadge,
  CertsContainer,
  ToolbarRow,
  ViewToggle,
  ViewToggleButton,
  ExportButton,
  IssuerSection,
  IssuerHeader,
  IssuerName,
  IssuerMeta,
  CertSection,
  CertHeader,
  CertName,
  CertMeta,
  TreeEmployeeRow
} from './styles';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import EmployeeSearch from '../../common/EmployeeSearch';
import { EmployeeSearchProvider } from '../../common/EmployeeSearch/contexts/EmployeeSearchProvider';
import CertificationsStats from './CertificationsStats';
import { cvManagementApi } from '../../../services/api/cvManagementApi';
import CertificationIssuerFilter from '../../common/filters/CertificationIssuerFilter';
import {
  ISSUER_TYPE_FILTER,
  EXPIRATION_OPTIONS,
  getExpirationLabel
} from '../../../constants/certificationFilters';
import {
  CERTIFICATION_CSV_FIELDS,
  CERTIFICATION_CSV_HEADERS,
  buildCertificationCsvRows,
  buildCertificationsCsvFilename
} from '../../../utils/certificationsReportCsv';
import { arrayToCSV, downloadCSV } from '../../../utils/csvExport';
import { toLocalDate } from '../../../utils/dateOnly';

const VIEW_MODE = {
  EMPLOYEE: 'employee',
  ISSUER: 'issuer'
};

const CertificationsReport = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState(VIEW_MODE.EMPLOYEE);
  const [loading, setLoading] = useState(true);

  // Employee view state
  const [employeeData, setEmployeeData] = useState([]);

  // Issuer view state
  const [issuerData, setIssuerData] = useState([]);
  const [expandedIssuers, setExpandedIssuers] = useState(new Set());
  const [expandedCerts, setExpandedCerts] = useState(new Set());

  // Shared state
  const [stats, setStats] = useState(null);

  // Filters
  const [categories, setCategories] = useState([]);
  const [selectedIssuerId, setSelectedIssuerId] = useState('');
  const [issuerTypeFilter, setIssuerTypeFilter] = useState(ISSUER_TYPE_FILTER.ALL);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [expirationStatus, setExpirationStatus] = useState('');
  const [certificationName, setCertificationName] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const categoriesData = await cvManagementApi.getAllCertificationCategories();
        setCategories(categoriesData || []);
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };
    fetchFilters();
  }, []);

  const filterParams = useCallback(() => ({
    ...(selectedIssuerId && { issuerId: selectedIssuerId }),
    ...(issuerTypeFilter !== ISSUER_TYPE_FILTER.ALL && { issuerType: issuerTypeFilter }),
    ...(selectedCategoryId && { categoryId: selectedCategoryId }),
    ...(expirationStatus && { expirationStatus }),
    ...(certificationName && { certificationName }),
    ...(selectedEmployee && { employeeId: selectedEmployee.id })
  }), [selectedIssuerId, issuerTypeFilter, selectedCategoryId, expirationStatus, certificationName, selectedEmployee]);

  // Employee view fetch
  const fetchEmployeeReport = useCallback(async () => {
    try {
      setLoading(true);

      const result = await cvManagementApi.getCertificationsReport(filterParams());

      setEmployeeData(result.data);
      setStats(result.stats);
    } catch (error) {
      console.error('Error fetching certifications report:', error);
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  // Issuer view fetch
  const fetchIssuerReport = useCallback(async () => {
    try {
      setLoading(true);
      const result = await cvManagementApi.getCertificationsReportByIssuer(filterParams());
      setIssuerData(result.data || []);
      setStats(result.stats);
      setExpandedIssuers(new Set());
      setExpandedCerts(new Set());
    } catch (error) {
      console.error('Error fetching certifications report by issuer:', error);
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  // Fetch on view mode or filter change
  useEffect(() => {
    if (viewMode === VIEW_MODE.EMPLOYEE) {
      fetchEmployeeReport();
    } else {
      fetchIssuerReport();
    }
  }, [viewMode, fetchEmployeeReport, fetchIssuerReport]);

  /**
   * Canonical CSV rows for whichever view is active (ADR-0003 §D1): one row per
   * individual x certification record. Both views flatten to the same grain, so
   * the same filters produce the same file from either toggle.
   *
   * Derived from the loaded response rather than from what is expanded on
   * screen — a file whose contents depended on click history would not be
   * reproducible.
   */
  const exportRows = useMemo(
    () => buildCertificationCsvRows(viewMode, { employeeData, issuerData }),
    [viewMode, employeeData, issuerData]
  );

  const handleExportCsv = () => {
    if (exportRows.length === 0) {
      return;
    }

    const csvContent = arrayToCSV(exportRows, CERTIFICATION_CSV_FIELDS, {}, {
      headerLabels: CERTIFICATION_CSV_HEADERS,
      // Certification and issuer names are free text typed into CVs, so a value
      // beginning with `=` must not reach Excel as a formula.
      sanitizeFormulas: true
    });

    // BOM: without it Excel on Windows mojibakes Romanian and Polish names.
    downloadCSV(csvContent, buildCertificationsCsvFilename(), { bom: true });
  };

  const exportDisabled = loading || exportRows.length === 0;

  const exportTooltip = () => {
    if (loading) {
      return 'Loading...';
    }

    if (exportRows.length === 0) {
      return 'No data to export';
    }

    // Stating the row count pre-empts "I collapsed the tree, why did I get
    // 1,284 rows?" — the export covers all filtered rows, not the expanded ones.
    return `Exports all ${exportRows.length.toLocaleString()} ${exportRows.length === 1 ? 'row' : 'rows'} matching the current filters`;
  };

  const toggleIssuer = (issuerId) => {
    setExpandedIssuers(prev => {
      const next = new Set(prev);
      if (next.has(issuerId)) {
        next.delete(issuerId);
      } else {
        next.add(issuerId);
      }
      return next;
    });
  };

  const toggleCert = (key) => {
    setExpandedCerts(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const renderFilters = () => (
    <FiltersBar>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Issuer Type</InputLabel>
        <Select
          value={issuerTypeFilter}
          label="Issuer Type"
          onChange={(e) => {
            const newValue = e.target.value;
            setIssuerTypeFilter(newValue);
            if (newValue === ISSUER_TYPE_FILTER.CUSTOM) {
              setSelectedIssuerId('');
            }
          }}
        >
          <MenuItem value={ISSUER_TYPE_FILTER.ALL}>All</MenuItem>
          <MenuItem value={ISSUER_TYPE_FILTER.CUSTOM}>Custom issuers</MenuItem>
          <MenuItem value={ISSUER_TYPE_FILTER.STANDARD}>Standard issuers</MenuItem>
        </Select>
      </FormControl>

      <CertificationIssuerFilter
        value={selectedIssuerId}
        onChange={setSelectedIssuerId}
        disabled={issuerTypeFilter === ISSUER_TYPE_FILTER.CUSTOM}
      />

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Category</InputLabel>
        <Select
          value={selectedCategoryId}
          onChange={(e) => setSelectedCategoryId(e.target.value)}
          label="Category"
        >
          <MenuItem value="">All Categories</MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Expiration</InputLabel>
        <Select
          value={expirationStatus}
          onChange={(e) => setExpirationStatus(e.target.value)}
          label="Expiration"
        >
          {EXPIRATION_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        placeholder="Search certification name..."
        variant="outlined"
        size="small"
        value={certificationName}
        onChange={(e) => setCertificationName(e.target.value)}
        autoComplete="off"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaSearch style={{ color: '#6B7280' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            height: '40px',
            fontSize: '14px'
          }
        }}
      />

      <EmployeeSearchProvider instanceId="cert-report-search">
        <EmployeeSearch
          value={selectedEmployee}
          onChange={setSelectedEmployee}
          label="Individual"
          placeholder="Search individuals..."
          width={250}
          height={40}
          hideLabel
        />
      </EmployeeSearchProvider>
    </FiltersBar>
  );


  const renderEmployeeView = () => {
    if (employeeData.length === 0) {
      return (
        <EmptyState
          title="No results found"
          description="No individuals match the selected filters. Try adjusting your criteria."
        />
      );
    }
    return (
      <>
        {employeeData.map((employee) => (
          <EmployeeRow key={employee.employeeId}>
            <EmployeeInfo>
              <EmployeeAvatar
                employee={{ azureId: employee.azureId, fullName: employee.fullName }}
                size={36}
              />
              <Box sx={{ overflow: 'hidden' }}>
                <EmployeeName>{employee.fullName}</EmployeeName>
                <EmployeeEmail>{employee.email}</EmployeeEmail>
                <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '11px' }}>
                  {employee.certifications.length} {employee.certifications.length === 1 ? 'certification' : 'certifications'}
                </Typography>
              </Box>
            </EmployeeInfo>

            <CertsContainer>
              {employee.certifications.map((cert) => (
                <Tooltip
                  key={cert.id}
                  title={
                    <Box>
                      {cert.issuer && <div>Issuer: {cert.issuer.name}</div>}
                      {cert.category && <div>Category: {cert.category.name}</div>}
                      {cert.issuedDate && <div>Issued: {toLocalDate(cert.issuedDate)?.toLocaleDateString()}</div>}
                      {cert.expirationDate && <div>Expires: {toLocalDate(cert.expirationDate)?.toLocaleDateString()}</div>}
                    </Box>
                  }
                  arrow
                >
                  <CertBadge $status={cert.expirationStatus}>
                    {cert.name}
                    <Typography variant="caption" sx={{ opacity: 0.7, ml: 0.5 }}>
                      ({getExpirationLabel(cert.expirationStatus)})
                    </Typography>
                  </CertBadge>
                </Tooltip>
              ))}
            </CertsContainer>
          </EmployeeRow>
        ))}

      </>
    );
  };

  const renderIssuerView = () => {
    if (issuerData.length === 0) {
      return (
        <EmptyState
          title="No results found"
          description="No certifications match the selected filters. Try adjusting your criteria."
        />
      );
    }
    return (
      <>
        {issuerData.map((issuer) => (
          <IssuerSection key={issuer.id}>
            <IssuerHeader onClick={() => toggleIssuer(issuer.id)}>
              <IssuerName>
                {expandedIssuers.has(issuer.id) ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                {issuer.name}
                <Chip
                  label={`${issuer.certifications.length} ${issuer.certifications.length === 1 ? 'certification' : 'certifications'}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '12px', height: 22 }}
                />
              </IssuerName>
              <IssuerMeta>
                {issuer.employeeCount} {issuer.employeeCount === 1 ? 'individual' : 'individuals'}
              </IssuerMeta>
            </IssuerHeader>

            {expandedIssuers.has(issuer.id) && issuer.certifications.map((cert) => {
              const certKey = `${issuer.id}::${cert.name}`;
              return (
                <CertSection key={certKey}>
                  <CertHeader onClick={() => toggleCert(certKey)}>
                    <CertName>
                      {expandedCerts.has(certKey) ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                      {cert.name}
                      {cert.category && (
                        <Chip
                          label={cert.category.name}
                          size="small"
                          sx={{ fontSize: '11px', height: 20, backgroundColor: '#f1f5f9' }}
                        />
                      )}
                    </CertName>
                    <CertMeta>
                      {cert.employeeCount} {cert.employeeCount === 1 ? 'individual' : 'individuals'}
                    </CertMeta>
                  </CertHeader>

                  {expandedCerts.has(certKey) && cert.employees.map((emp) => (
                    <TreeEmployeeRow key={emp.employeeId}>
                      <EmployeeInfo>
                        <EmployeeAvatar
                          employee={{ azureId: emp.azureId, fullName: emp.fullName }}
                          size={28}
                        />
                        <Box>
                          <EmployeeName>{emp.fullName}</EmployeeName>
                          <EmployeeEmail>{emp.email}</EmployeeEmail>
                        </Box>
                      </EmployeeInfo>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {emp.issuedDate && (
                          <Typography variant="caption" color="text.secondary">
                            Issued: {toLocalDate(emp.issuedDate)?.toLocaleDateString()}
                          </Typography>
                        )}
                        {emp.expirationDate && (
                          <Typography variant="caption" color="text.secondary">
                            Expires: {toLocalDate(emp.expirationDate)?.toLocaleDateString()}
                          </Typography>
                        )}
                        <CertBadge $status={emp.expirationStatus} style={{ fontSize: '12px', padding: '2px 10px' }}>
                          {getExpirationLabel(emp.expirationStatus)}
                        </CertBadge>
                      </Box>
                    </TreeEmployeeRow>
                  ))}
                </CertSection>
              );
            })}
          </IssuerSection>
        ))}
      </>
    );
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Certifications Report</Title>
        </HeaderLeft>
        <CertificationsStats stats={stats} showIssuers={viewMode === VIEW_MODE.ISSUER} />
      </Header>

      <Content>
        <MainSection>
          {renderFilters()}

          <ToolbarRow>
            <Tooltip title={exportTooltip()} arrow>
              <span>
                <ExportButton
                  onClick={handleExportCsv}
                  disabled={exportDisabled}
                  aria-label="Export certifications report as CSV"
                >
                  <FaDownload /> Export CSV
                </ExportButton>
              </span>
            </Tooltip>

            <ViewToggle>
              <ViewToggleButton
                $active={viewMode === VIEW_MODE.EMPLOYEE}
                onClick={() => setViewMode(VIEW_MODE.EMPLOYEE)}
              >
                <PeopleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                By Individual
              </ViewToggleButton>
              <ViewToggleButton
                $active={viewMode === VIEW_MODE.ISSUER}
                onClick={() => setViewMode(VIEW_MODE.ISSUER)}
              >
                <AccountTreeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                By Issuer
              </ViewToggleButton>
            </ViewToggle>
          </ToolbarRow>

          {loading ? (
            <LoadingSpinner message="Loading certifications report..." />
          ) : viewMode === VIEW_MODE.EMPLOYEE ? (
            renderEmployeeView()
          ) : (
            renderIssuerView()
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default CertificationsReport;
